<template>
  <div class="users-page">
    <APageHead title="관리자 계정 관리" sub="시스템 관리자 및 운영자 계정을 관리합니다.">
      <template #actions>
        <ABtn kind="primary" icon="person_add" @click="addNewManager">새 관리자 추가</ABtn>
      </template>
    </APageHead>

    <!-- KPI -->
    <div class="users-page__stats">
      <ACard><AStat label="전체 관리자" :value="formatNum(totalCount)" unit="명" /></ACard>
      <ACard><AStat label="활성 계정" :value="formatNum(activeCount)" unit="명" /></ACard>
      <ACard><AStat label="비활성 계정" :value="formatNum(inactiveCount)" unit="명" /></ACard>
    </div>

    <!-- 검색 -->
    <ACard overflow="visible">
      <SearchArea :search-fields="searchFields" @search="handleSearch" />
    </ACard>

    <!-- 목록 -->
    <ACard>
      <template #title>
        <span class="users-page__title">{{ $t('navi.03_01') }}</span>
      </template>
      <template #sub>관리자 계정 목록 및 상태 관리</template>
      <template #action>
        <APill kind="info">총 {{ formatNum(totalCount) }}명</APill>
      </template>

      <AEmpty
        v-if="tableData.length === 0"
        title="등록된 관리자가 없어요"
        sub="새 관리자 추가 버튼을 눌러 시작하세요."
      />

      <ATable
        v-else
        :columns="columns"
        :rows="tableData"
        row-key="idx"
      >
        <template #cell:no="{ index }">
          <span class="users-page__num">{{ getItemNumber(index) }}</span>
        </template>
        <template #cell:emp="{ row }">
          <span class="users-page__mono">{{ row.emp_number }}</span>
        </template>
        <template #cell:name="{ row }">
          <span class="users-page__name">{{ row.name }}</span>
        </template>
        <template #cell:email="{ row }">
          <span class="users-page__mono">{{ row.email }}</span>
        </template>
        <template #cell:phone="{ row }">
          <span>{{ row.phone }}</span>
        </template>
        <template #cell:status="{ row }">
          <APill v-if="row.active_yn === 'Y'" kind="ok" dot>{{ $t('label.65') }}</APill>
          <APill v-else kind="warn" dot>{{ $t('label.66') }}</APill>
        </template>
        <template #cell:actions="{ row }">
          <div class="users-page__actions">
            <ABtn
              v-if="isSuperAdmin"
              size="sm"
              icon="app_settings_alt"
              icon-only
              title="앱 교재 설정"
              @click="openAppTextbookSettings(row)"
            />
            <ABtn size="sm" icon="visibility" @click="viewDetail(row)">{{ $t('btn.24') }}</ABtn>
          </div>
        </template>
      </ATable>

      <APagination
        v-if="totalCount > 0"
        :current-page="currentPage"
        :total="totalCount"
        :page-size="itemsPerPage"
        @change="(p) => { currentPage = p; getData(); }"
      />
    </ACard>

    <!-- 새 관리자 추가 모달 (자식 컴포넌트) -->
    <ManagerCreateModal
      :visible="showAddModal"
      @close="showAddModal = false"
      @success="getData"
    />

    <!-- 관리자별 앱 교재 설정 모달 (최고관리자 전용) -->
    <AppTextbookSettingsModal
      :visible="showAppTextbookModal"
      :employee="appTextbookTarget"
      @close="showAppTextbookModal = false"
    />
  </div>
</template>

<script>
import SearchArea from "@/components/SearchArea.vue";
import ManagerCreateModal from "@/components/Users/ManagerCreateModal.vue";
import AppTextbookSettingsModal from "@/components/Users/AppTextbookSettingsModal.vue";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";

export default {
  name: "UsersManagers",
  components: { SearchArea, ManagerCreateModal, AppTextbookSettingsModal, APageHead, ABtn, ACard, AStat, APill, ATable, AEmpty, APagination },
  data() {
    return {
      tableData: [],
      currentPage: 1,
      itemsPerPage: 10,
      totalCount: 0,
      searchValues: {},
      showAddModal: false,
      showAppTextbookModal: false,
      appTextbookTarget: null,
      searchFields: [
        { key: "name", placeholder: this.$t("label.06"), col: "col-md-2" },
        { key: "email", placeholder: this.$t("label.01"), col: "col-md-2" },
        {
          key: "active_yn",
          placeholder: this.$t("label.51"),
          col: "col-md-2",
          type: "select",
          options: [
            { value: "Y", label: this.$t("label.65") },
            { value: "N", label: this.$t("label.66") },
          ],
        },
      ],
      columns: [
        { key: "no", label: "번호", align: "center", width: 60 },
        { key: "emp", label: this.$t("label.18"), width: 130 },
        { key: "name", label: this.$t("label.06"), width: 140 },
        { key: "email", label: this.$t("label.01") },
        { key: "phone", label: this.$t("label.10"), width: 140 },
        { key: "status", label: this.$t("label.51"), width: 110 },
        { key: "actions", label: "액션", align: "right", width: 110 },
      ],
    };
  },
  computed: {
    userInfo() { return this.$store.state.auth.user_info || {}; },
    isSuperAdmin() { return this.$store.getters["auth/isSuperAdmin"]; },
    getItemNumber() {
      return (index) => this.totalCount - ((this.currentPage - 1) * this.itemsPerPage + index);
    },
    activeCount() { return this.tableData.filter((i) => i.active_yn === "Y").length; },
    inactiveCount() { return this.tableData.filter((i) => i.active_yn === "N").length; },
  },
  mounted() {
    if (this.userInfo && this.userInfo.name) this.getData();
  },
  methods: {
    formatNum(n) { return Number(n || 0).toLocaleString(); },
    async getData() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/roles/employees",
          params: {
            limit: this.itemsPerPage,
            page: this.currentPage,
            search_params: JSON.stringify(this.searchValues),
          },
          loadingBar: true,
        });
        if (res.status === 200) {
          this.tableData = res.data.items;
          this.totalCount = res.data.total_count;
        }
      } catch (error) {
        console.error("Error :", error);
      }
    },
    handleSearch(searchValues) {
      this.searchValues = searchValues;
      this.currentPage = 1;
      this.getData();
    },
    viewDetail(item) {
      this.$router.push({ name: "ManagerDetail", query: { idx: item.idx } });
    },
    addNewManager() { this.showAddModal = true; },
    openAppTextbookSettings(item) {
      this.appTextbookTarget = item;
      this.showAppTextbookModal = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.users-page {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    @media (max-width: 992px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__title {
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__num { font-family: $p-font-mono; color: $p-ink-mid; font-weight: 700; }
  &__actions { display: inline-flex; gap: 4px; justify-content: flex-end; }
  &__mono { font-family: $p-font-mono; color: $p-ink2; font-size: 12px; }
  &__name { font-weight: 700; color: $p-ink; }
}
</style>
